import type { TBBox, TMat2D, TOriginX, TOriginY, TRadian } from '../types';
import { halfPI } from './constants';
import { Point, type XY } from './point';

/**
 * Calculate the cos of an angle, avoiding returning floats for known results
 * This function is here just to avoid getting 0.999999999999999 when dealing
 * with numbers that are really 1 or 0.
 * @param {TRadian} angle the angle
 * @return {Number} the cosin value for angle.
 */
export const cos = (angle: TRadian): number => {
    if (angle === 0) {
        return 1;
    }
    const angleSlice = Math.abs(angle) / halfPI;
    switch (angleSlice) {
        case 1:
        case 3:
            return 0;
        case 2:
            return -1;
    }
    return Math.cos(angle);
};

/**
 * Calculate the cos of an angle, avoiding returning floats for known results
 * This function is here just to avoid getting 0.999999999999999 when dealing
 * with numbers that are really 1 or 0.
 * @param {TRadian} angle the angle
 * @return {Number} the sin value for angle.
 */
export const sin = (angle: TRadian): number => {
    if (angle === 0) {
        return 0;
    }
    const angleSlice = angle / halfPI;
    const value = Math.sign(angle);
    switch (angleSlice) {
        case 1:
            return value;
        case 2:
            return 0;
        case 3:
            return -value;
    }
    return Math.sin(angle);
};

/**
 * Calculates bounding box (left, top, width, height) from given `points`
 * @param {XY[]} points
 * @return {Object} Object with left, top, width, height properties
 */
export const makeBoundingBoxFromPoints = (points: XY[]): TBBox => {
    let left = 0,
        top = 0,
        width = 0,
        height = 0;

    for (let i = 0, len = points.length; i < len; i++) {
        const { x, y } = points[i];
        if (x > width || !i) width = x;
        if (x < left || !i) left = x;
        if (y > height || !i) height = y;
        if (y < top || !i) top = y;
    }

    return {
        left,
        top,
        width: width - left,
        height: height - top,
    };
};

const originOffset = {
    left: -0.5,
    top: -0.5,
    center: 0,
    bottom: 0.5,
    right: 0.5,
};
/**
 * Resolves origin value relative to center
 * @private
 * @param {TOriginX | TOriginY} originValue originX / originY
 * @returns number
 */
export const resolveOrigin = (originValue: TOriginX | TOriginY | number): number =>
    typeof originValue === 'string' ? originOffset[originValue] : originValue - 0.5;

/**
 * given a width and height, return the size of the bounding box
 * that can contains the box with width/height with applied transform.
 * Use to calculate the boxes around objects for controls.
 * @param {Number} width
 * @param {Number} height
 * @param {TMat2D} t
 * @returns {Point} size
 */
export const sizeAfterTransform = (width: number, height: number, t: TMat2D) => {
    const dimX = width / 2,
        dimY = height / 2,
        points = [new Point(-dimX, -dimY), new Point(dimX, -dimY), new Point(-dimX, dimY), new Point(dimX, dimY)].map(
            (p) => p.transform(t),
        ),
        bbox = makeBoundingBoxFromPoints(points);
    return new Point(bbox.width, bbox.height);
};
